import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, deleteTenant, listTenants, putTenant } from './api/client';
import type { TenantResponse } from './api/types';

function describeError(e: unknown): string {
  return e instanceof ApiError ? `${e.body.code}: ${e.body.description}` : 'Request failed';
}

const left = { textAlign: 'left' } as const;

/** Admin view of the whole tenant directory: list / create / delete tenants and jump into managing one (the same tenant-scoped view a tenant's own admins get). */
export function TenantsPanel({ onManage }: { onManage: (tenantId: string) => void }) {
  const [tenants, setTenants] = useState<TenantResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // create form
  const [tenantId, setTenantId] = useState('');
  const [displayName, setDisplayName] = useState('');

  async function load(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setTenants(await listTenants());
    } catch (e: unknown) {
      setError(describeError(e));
      setTenants([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await putTenant(tenantId, { displayName });
      setTenantId('');
      setDisplayName('');
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  async function onDelete(id: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteTenant(id);
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>
        Tenants{' '}
        <button onClick={() => void load()} disabled={busy}>
          Reload
        </button>
      </h2>

      {error !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {error}
        </p>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={left}>Tenant ID</th>
            <th style={left}>Display name</th>
            <th style={left}>Role</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tenants.length === 0 ? (
            <tr>
              <td colSpan={4}>
                <em>No tenants yet.</em>
              </td>
            </tr>
          ) : (
            tenants.map((t) => (
              <tr key={t.tenantId}>
                <td>{t.tenantId}</td>
                <td>{t.displayName}</td>
                <td>{t.isInternal ? <strong>internal (admin)</strong> : 'tenant'}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => onManage(t.tenantId)} disabled={busy}>
                    Manage
                  </button>{' '}
                  <button onClick={() => void onDelete(t.tenantId)} disabled={busy || t.isInternal}>
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h3>Add tenant</h3>
      <form onSubmit={(e) => void onCreate(e)} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input required placeholder="tenant id" value={tenantId} onChange={(e) => setTenantId(e.currentTarget.value)} />
        <input
          required
          placeholder="display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.currentTarget.value)}
        />
        <button type="submit" disabled={busy}>
          Create
        </button>
      </form>
    </section>
  );
}
